// @ts-expect-error
import convertIso3ToIso2 from 'country-iso-3-to-2'

/**
 * Truncate a string to a certain length
 *
 * @export
 * @param {string} str
 * @param {number} [n=15]
 * @return {*} 
 */
export function truncate(str: string, n: number = 15) {
  return str.length > n ? `${str.substring(0, n - 3)}...` : str
}


/**
 * Capitalize a string
 *
 * @export
 * @param {string} string
 * @return {*}  {string}
 */
export function capitalize(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

/**
 * Get the region name of a country by its alpha-2 or alpha-3 code
 *
 * @export
 * @param {string} countryCodeAlpha2
 * @return {*}  {string}
 */
const regionNamesFormatter = new Intl.DisplayNames(['fr-FR'], { type: 'region' })
export function getRegionNameByAlphaCode(countryCodeAlpha: string): string {
  try {
    if (countryCodeAlpha.length === 2) {
      return regionNamesFormatter.of(countryCodeAlpha) ?? countryCodeAlpha
    } else {
      const countryCodeAlpha2: string = convertIso3ToIso2(countryCodeAlpha) ?? countryCodeAlpha
      return regionNamesFormatter.of(countryCodeAlpha2) ?? countryCodeAlpha
    }
  } catch (e: any) {
    return countryCodeAlpha
  }
}

/**
 * Define if a country is eligible for the PEA
 *
 * @export
 * @param {string} countryCode
 * @return {*}  {boolean}
 */
const eeaCountryCodes = [
  "AUT", // Austria
  "BEL", // Belgium
  "BGR", // Bulgaria
  "HRV", // Croatia
  "CYP", // Cyprus
  "CZE", // Czech Republic
  "DNK", // Denmark
  "EST", // Estonia
  "FIN", // Finland
  "FRA", // France
  "DEU", // Germany
  "GRC", // Greece
  "HUN", // Hungary
  "IRL", // Ireland
  "ITA", // Italy
  "LVA", // Latvia
  "LTU", // Lithuania
  "LUX", // Luxembourg
  "MLT", // Malta
  "NLD", // Netherlands
  "POL", // Poland
  "PRT", // Portugal
  "ROU", // Romania
  "SVK", // Slovakia
  "SVN", // Slovenia
  "ESP", // Spain
  "SWE", // Sweden
  "ISL", // Iceland
  "LIE", // Liechtenstein
  "NOR"  // Norway
]
export function isCountryEligibleForPEA(countryCode: string): boolean {
  return eeaCountryCodes.includes(countryCode)
}
export const peaEligibleCountriesList: ReadonlyArray<string> = Object.freeze(eeaCountryCodes)

/**
 * Get the french name of a sector
 *
 * @export
 * @param {string} sectorCode
 * @return {*}  {string}
 */
const sectorTranslations: Record<string, string> = {
  "Communication Services": "Communication",
  "Consumer Disc": "Conso Facult.",
  "Energy": "Énergie",
  "Consumer Cyclical": "Conso. Cyclique",
  "Consumer Defensive": "Conso. Défensive",
  "Healthcare": "Santé",
  "Info Tech": "Tech",
  "Basic Materials": "Matériaux",
  "Real Estate": "Immobilier",
  "Utilities": "Fournisseurs",
  "Financial Services": "Services financiers",
  "Industrials": "Industriels",
  "Technology": "Technologie"
};
export function getFrenchSectorName(sectorCode: string): string | undefined {
  return sectorTranslations[sectorCode]
}

/**
 * Get the french name of an industry
 *
 * @export
 * @param {string} industryCode
 * @return {*}  {string}
 */
const industryTranslations: Record<string, string> = {
  "Software - Infrastructure": "Logiciel Infra",
  "Consumer Electronics": "Tech Conso",
  "Semiconductors": "Semi-conducteurs",
  "Internet Content & Information": "Contenu Web",
  "Internet Retail": "E-Commerce",
  "Insurance - Diversified": "Assurance",
  "Drug Manufacturers - General": "Pharma",
  "Banks - Diversified": "Banques",
  "Credit Services": "Crédit",
  "Auto Manufacturers": "Automobile",
  "Discount Stores": "Discount",
  "Oil & Gas Integrated": "Pétro-Gaz",
  "Healthcare Plans": "Assurance Santé",
  "Household & Personal Products": "Conso Perso",
  "Home Improvement Retail": "Bricolage",
  "Software - Application": "Logiciel Appli",
  "Entertainment": "Divertissement",
  "Beverages - Non-Alcoholic": "Boissons",
  "Specialty Chemicals": "Chimie",
  "Diagnostics & Research": "Diagnostic",
  "Information Technology Services": "IT",
  "Restaurants": "Restauration",
  "Communication Equipment": "Communication",
  "Specialty Industrial Machinery": "Machinerie",
  "Medical Devices": "Dispo. Médicaux",
  "Telecom Services": "Télécom",
  "Farm & Heavy Construction Machinery": "Machinerie lourde",
  "Semiconductor Equipment & Materials": "Matériel Semi-condu",
  "Capital Markets": "Capitaux",
  "Oil & Gas E&P": "Exploration Pétrolière",
  "Railroads": "Ferroviaire",
  "Tobacco": "Tabac",
  "Footwear & Accessories": "Chaussures",
  "Medical Instruments & Supplies": "Instrument Médicaux",
  "Financial Data & Stock Exchanges": "Financier",
  "Conglomerates": "Conglomérats",
  "Utilities - Regulated Electric": "Fourn. Électricité",
  "Aerospace & Defense": "Aérospatiale",
  "Integrated Freight & Logistics": "Logistique",
  "Travel Services": "Voyage",
  "Asset Management": "Gestion Actifs",
  "Insurance - Property & Casualty": "Assurances Biens",
  "REIT - Industrial": "SIIC Industriel",
  "Apparel Retail": "Mode",
  "Biotechnology": "Biotech",
  "Staffing & Employment Services": "RH",
  "Insurance Brokers": "Courtage",
  "Confectioners": "Confiserie",
  "REIT - Specialty": "SIIC Spécialisé",
  "Computer Hardware": "Informatique",
  "Medical Care Facilities": "Soins",
  "Waste Management": "Déchets",
  "Oil & Gas Equipment & Services": "Pétro-Services",
  "Drug Manufacturers - Specialty & Generic": "Pharma",
  "Lodging": "Hôtellerie",
  "Oil & Gas Refining & Marketing": "Pétro-Raffinage",
  "Medical Distribution": "Distrib. Médicale",
  "Specialty Business Services": "Services Entreprises",
  "Electronic Components": "Composants",
  "Banks - Regional": "Banques Régionales",
  "Building Products & Equipment": "Construction",
  "Specialty Retail": "Commerce Spé.",
  "Copper": "Cuivre",
  "Utilities - Renewable": "Renouvelables",
  "Residential Construction": "Construction Resid.",
  "Insurance - Life": "Vie",
  "REIT - Healthcare Facilities": "SIIC Santé",
  "REIT - Retail": "SIIC Commerce",
  "Industrial Distribution": "Distribution Indu.",
  "Beverages - Wineries & Distilleries": "Vins",
  "Rental & Leasing Services": "Location",
  "Steel": "Acier",
  "Oil & Gas Midstream": "Pétro-Midstream",
  "Utilities - Diversified": "Fournisseurs-Divers",
  "Packaged Foods": "Alimentaire",
  "Airlines": "Compagnies Aériennes",
  "Farm Products": "Produits Agricoles",
  "Insurance - Specialty": "Assurance Spécialisée",
  "Security & Protection Services": "Services de Sécurité",
  "Packaging & Containers": "Packaging",
  "Auto Parts": "Pièces Auto",
  "REIT - Office": "Immo - Bureaux",
  "Utilities - Regulated Gas": "Fournisseurs Gaz",
  "REIT - Residential": "Immo - Résidentiel",
  "Utilities - Regulated Water": "Fournisseur Eau",
  "Real Estate Services": "Services Immobiliers",
  "Chemicals": "Produits Chimiques",
  "Agricultural Inputs": "Intrants agricoles",
  "Resorts & Casinos": "Resort et Casino",
  "Electronic Gaming & Multimedia": "Jeux vidéos",
  "Consulting Services": "Consulting",
  "Insurance - Reinsurance": "Assurance - Réassurance",
  "Solar": "Énergie Solaire",
  "Scientific & Technical Instruments": "Instruments Scientifiques",
  "Health Information Services": "Services Info Santé",
  "Leisure": "Loisirs",
  "REIT - Hotel & Motel": "Immo - Hôtel & Motel",
  "Electrical Equipment & Parts": "Équipements Électriques",
  "Advertising Agencies": "Agences de publicité",
  "Engineering & Construction": "Ingénierie et construction",
  "Auto & Truck Dealerships": "Concession auto et camions",
  "Grocery Stores": "Supermarchés",
  "Furnishings, Fixtures & Appliances": "Ameublement et Appareils",
  "Building Materials": "Matériaux de construction",
  "Gold": "Or",
  "Utilities - Independent Power Producers": "Fournisseurs - Prod Indé",
  "Trucking": "Camion",
  "Apparel Manufacturing": "Vêtement",
  "Personal Services": "Services personnels",
  "Tools & Accessories": "Outils et accessoires",
  "Food Distribution": "Distribution alimentaire",
  "Beverages - Brewers": "Boissons - Brasseries",
  "Luxury Goods": "Luxe",
  "REIT - Diversified": "Immo - Diversifié",
  "Pollution & Treatment Controls": "Contrôle Pollution",
  "Pharmaceutical Retailers": "Détaillants Pharma",
  "Lumber & Wood Production": "Bois",
  "Electronics & Computer Distribution": "Électronique",
  "Paper & Paper Products": "Papier",
  "Marine Shipping": "Maritime",
  "Aluminum": "Aluminium",
  "Other Industrial Metals & Mining": "Métaux & Minerais",
  "Airports & Air Services": "Aéroports",
  "Uranium": "Uranium",
  "Gambling": "Jeux",
  "Education & Training Services": "Éducation",
  "Infrastructure Operations": "Infrastructure",
  "Publishing": "Édition"
};
export function getFrenchIndustryName(industryCode: string): string | undefined {
  return industryTranslations[industryCode]
}